import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../services/firebase';
import { setAuthToken, syncUserAccount } from '../services/api';
import { useAuth } from '../features/auth';
import { useToast } from '../features/toast';
import { logSignUp, logLogin } from '../services/analytics';

const highlightItems = [
  {
    icon: '🧸',
    title: 'Authentic Gachapon',
    copy: 'Real capsules, straight from Japan.',
    description: 'Every spin wins a real collectible.',
  },
  {
    icon: '🔐',
    title: 'Token Wallet',
    copy: 'Your tokens stay synced across all your devices.',
    description: 'Play anywhere, your spins follow you.',
  },
  {
    icon: '🗄️',
    title: 'Personal Locker',
    copy: 'Every item from your spin is safely stored in your locker.',
    description: 'Collect up to 8 and unlock free shipping.',
  },
  {
    icon: '🚚',
    title: 'Free Shipping Reward',
    copy: 'Ship your full locker — shipping\'s on us!',
    description: 'Can\'t wait? Ship anytime for just $6.99.',
  },
];

const UserLogin = ({ hideHighlights = false, initialMode, mobileFullHeight = false, compact = false, hideExplore = false }) => {
  const { login, skipLogin, loginInitialMode, setLoginPromptOpen, resendVerification, sendPasswordReset } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [mode, setMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasVerificationEmailSent, setHasVerificationEmailSent] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [hasPasswordResetEmailSent, setHasPasswordResetEmailSent] = useState(false);
  const [isResetResendLoading, setIsResetResendLoading] = useState(false);

  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const isSignup = mode === 'signup';

  useEffect(() => {
    const preferred = initialMode || loginInitialMode;
    if (preferred === 'signin' || preferred === 'signup') {
      setMode(preferred);
    }
  }, [loginInitialMode, initialMode]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (currentUser) {
          await currentUser.reload();
          if (currentUser.emailVerified) {
            showToast({
              message: 'Email verified! Welcome 🎉',
              tone: 'info',
            });
          }
        }
      } catch (err) {
        // Swallow errors to keep UX uninterrupted.
      }
    });
    return () => unsubscribe();
  }, [showToast]);

  const friendlyAuthMessages = {
    'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/weak-password': 'Password should be at least 6 characters.',
    'auth/user-not-found': 'No user with this email. Check for typos or sign up first.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/popup-closed-by-user': 'Google sign-in was closed before completing.',
    'auth/redirect-cancelled-by-user': 'Google sign-in was cancelled. Please try again.',
  };

  const resolveErrorMessage = (errorObj, fallbackMessage) => {
    if (errorObj?.code && friendlyAuthMessages[errorObj.code]) {
      return friendlyAuthMessages[errorObj.code];
    }
    if (errorObj?.message) {
      return errorObj.message;
    }
    return fallbackMessage;
  };

  const handleAuthSuccess = async (firebaseUser, overrideProfile = {}, isNewUserSignup = false) => {
    try {
      // CHANGED: Reload user first to ensure fresh data, then get token
      try {
        await firebaseUser.reload();
      } catch (reloadError) {
        // Ignore reload errors, continue with existing user data
        console.warn('User reload failed (non-critical):', reloadError);
      }
      
      // CHANGED: Use getIdToken(true) to force refresh, with retry logic
      let token;
      try {
        token = await firebaseUser.getIdToken(true);
      } catch (tokenError) {
        // If forced refresh fails, try without force
        console.warn('Forced token refresh failed, trying normal:', tokenError);
        token = await firebaseUser.getIdToken(false);
      }
      setAuthToken(token);
      
      const profilePayload = {
        displayName:
          overrideProfile.displayName ?? firebaseUser.displayName ?? undefined,
        email: overrideProfile.email ?? firebaseUser.email ?? undefined,
        photoURL: overrideProfile.photoURL ?? firebaseUser.photoURL ?? undefined,
      };
      const response = await syncUserAccount(profilePayload);
      login(response.user);
      // CHANGED: Redirect to tutorial page for new users
      // Check isNewUserSignup flag first (from registration), then fallback to isExisting or message
      const isNewUser = isNewUserSignup || 
                        response.isExisting === false || 
                        (response.isExisting === undefined && response.message === 'User registered successfully');
      if (isNewUser) {
        navigate('/tutorial');
      }
    } catch (error) {
      console.error('Error in handleAuthSuccess:', error);
      // Still try to sync user account even if token fails
      try {
        const profilePayload = {
          displayName:
            overrideProfile.displayName ?? firebaseUser.displayName ?? undefined,
          email: overrideProfile.email ?? firebaseUser.email ?? undefined,
          photoURL: overrideProfile.photoURL ?? firebaseUser.photoURL ?? undefined,
        };
        const response = await syncUserAccount(profilePayload);
        login(response.user);
        // CHANGED: Also check isNewUserSignup in error fallback
        const isNewUser = isNewUserSignup || 
                          response.isExisting === false || 
                          (response.isExisting === undefined && response.message === 'User registered successfully');
        if (isNewUser) {
          navigate('/tutorial');
        }
      } catch (syncError) {
        console.error('Failed to sync user account:', syncError);
        setError('Failed to complete sign up. Please try again.');
      }
    }
  };

  const isEmailFieldError = useMemo(
    () => /email/i.test(error) && !/password/i.test(error),
    [error],
  );
  const isPasswordFieldError = useMemo(
    () => /password/i.test(error),
    [error],
  );

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!trimmedEmail) {
      setError('Enter your email to continue.');
      return;
    }

    const hasRequiredLength = typeof password === 'string' && password.length >= 8;
    const hasLetter = /[A-Za-z]/.test(password || '');
    const hasNumber = /\d/.test(password || '');
    if (!hasRequiredLength || !hasLetter || !hasNumber) {
      setError('Password must be 8+ characters with letters and numbers.');
      return;
    }

    setIsLoading(true);
    try {
      if (isSignup) {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          trimmedEmail,
          password,
        );
        try {
          await resendVerification();
          setHasVerificationEmailSent(true);
          showToast({
            message: "We've sent a verification link to your inbox.",
            tone: 'info',
          });
        } catch (_) {
          // Ignore send failure; user can resend later.
        }
        // CHANGED: Sync user account and redirect to tutorial for new users
        // Pass isNewUser flag to handleAuthSuccess to ensure redirect even if onAuthStateChanged already created the user
        await handleAuthSuccess(userCredential.user, {}, true);
        // CHANGED: Log analytics event for sign up
        logSignUp('email');
        // CHANGED: close the login modal after successful signup & verification email sent
        // Keep the session active; verification toast remains visible via toasts system
        try {
          if (typeof setLoginPromptOpen === 'function') {
            setLoginPromptOpen(false);
          }
        } catch (_) {}
      } else {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          trimmedEmail,
          password,
        );
        try {
          await userCredential.user.reload();
        } catch (_) {}
        await handleAuthSuccess(userCredential.user);
        // CHANGED: Log analytics event for login
        logLogin('email');
      }
    } catch (err) {
      setError(resolveErrorMessage(err, 'Unable to process email sign in.'));
    } finally {
      setIsLoading(false);
    }
  };

  // signInWithPopup; result handled here (setAuthToken, sync, login, new-user redirect)
  const handleGoogleSignIn = async () => {
    setError('');
    setIsGoogleLoading(true);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      const userCredential = await signInWithPopup(auth, provider);
      const isNewUser = userCredential.user.metadata?.creationTime === userCredential.user.metadata?.lastSignInTime;
      await handleAuthSuccess(userCredential.user, {}, isNewUser);
      if (isNewUser) {
        logSignUp('google');
      } else {
        logLogin('google');
      }
      try {
        if (typeof setLoginPromptOpen === 'function') {
          setLoginPromptOpen(false);
        }
      } catch (_) {}
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        setError(resolveErrorMessage(err, 'Google sign-in failed. Please try again.'));
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setError('');
    setIsResendLoading(true);
    try {
      await resendVerification();
      setHasVerificationEmailSent(true);
    } catch (err) {
      setError(resolveErrorMessage(err, 'Unable to resend verification email.'));
    } finally {
      setIsResendLoading(false);
    }
  };

  const handlePasswordResetRequest = async () => {
    setError('');
    const targetEmail = trimmedEmail;
    if (!targetEmail) {
      setError('Enter your email to continue.');
      return;
    }
    try {
      await sendPasswordReset(targetEmail);
      setHasPasswordResetEmailSent(true);
    } catch (err) {
      setError(resolveErrorMessage(err, 'Unable to send reset email. Please try again.'));
    }
  };

  const handleResendPasswordReset = async () => {
    setError('');
    const targetEmail = trimmedEmail;
    if (!targetEmail) {
      setError('Enter your email to continue.');
      return;
    }
    setIsResetResendLoading(true);
    try {
      await sendPasswordReset(targetEmail);
      setHasPasswordResetEmailSent(true);
    } catch (err) {
      setError(resolveErrorMessage(err, 'Unable to resend reset email.'));
    } finally {
      setIsResetResendLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(isSignup ? 'signin' : 'signup');
    setError('');
    setPassword('');
  };

  return (
    <section className="relative w-full">
      <div className={`relative overflow-hidden ${mobileFullHeight ? 'rounded-none sm:rounded-[32px]' : 'rounded-[32px]'} border border-white/60 bg-white/70 shadow-floating backdrop-blur-xl ${mobileFullHeight ? 'h-[100dvh] sm:h-auto' : ''}`}>
        <div className="pointer-events-none absolute -left-28 -top-24 h-56 w-56 rounded-full bg-ponpon-coral/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-28 top-1/2 h-60 w-60 -translate-y-1/2 rounded-full bg-ponpon-teal/15 blur-3xl" />
        <div className={`relative grid ${compact ? 'min-h-[360px]' : 'min-h-[450px]'} content-end items-stretch ${compact ? 'gap-6 px-4 pb-6 pt-10' : 'gap-10 px-6 pb-10 pt-14'} sm:px-10 lg:grid-cols-[1.1fr_1fr] lg:gap-12 lg:px-16 lg:pb-12 lg:pt-16`}>
          <div className="flex h-full flex-col gap-6">
            <div className="flex flex-col items-center gap-4 text-center sm:items-start sm:text-left">
              <div className="flex items-center justify-center sm:justify-start">
                <img
                  src="/assets/ponpon-logo.svg"
                  alt="PonPon!"
                  className="h-10 w-auto sm:h-12"
                />
              </div>
              <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
                A New Way to Experience Gachapon
              </h1>
            </div>

            {hideHighlights ? null : (
            <div className="mt-auto grid grid-cols-2 gap-3 sm:gap-4">
              {highlightItems.map((item) => (
                <div
                  key={item.title}
                  className="flex h-full flex-col gap-1.5 rounded-2xl border border-white/70 bg-white/85 p-3.5 text-center shadow-sm backdrop-blur sm:text-left"
                >
                  <span className="text-lg">{item.icon}</span>
                  <p className="text-sm font-semibold text-ponpon-navy">{item.title}</p>
                  <p className="text-xs font-semibold text-ponpon-navy/60">{item.copy}</p>
                </div>
              ))}
            </div>
            )}
          </div>

          <div className="flex h-full flex-col items-center">
            <div className={`relative flex h-full w-full ${compact ? 'min-h-[360px]' : 'min-h-[450px]'} flex-col rounded-[24px] border border-ponpon-teal/20 bg-white/95 ${compact ? 'p-4' : 'p-6'} shadow-lg sm:p-7`}>
              <div className="space-y-1 text-center">
                <h2 className="font-display text-2xl font-semibold text-ponpon-navy">
                  {isSignup ? 'Join to start your first spin' : 'Welcome back'}
                </h2>
              </div>

              <form onSubmit={handleEmailSubmit} className="mt-6 flex flex-1 flex-col gap-4">
                <div className="text-left">
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Email"
                    aria-label="Email"
                    className={`w-full rounded-2xl border border-ponpon-teal/25 bg-white px-4 py-3 text-sm font-semibold text-ponpon-navy/70 shadow-sm outline-none transition placeholder:text-ponpon-navy/50 focus:border-ponpon-coral focus:text-ponpon-navy focus:ring-2 focus:ring-ponpon-coral/20 ${isEmailFieldError ? 'border-ponpon-coral ring-2 ring-ponpon-coral/30' : ''}`}
                  />
                  {error && isEmailFieldError ? (
                    <p className="mt-1 text-xs font-medium text-ponpon-coral/60">{error}</p>
                  ) : null}
                </div>

                <div className="text-left">
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Password"
                    aria-label="Password"
                    className={`w-full rounded-2xl border border-ponpon-teal/25 bg-white px-4 py-3 text-sm font-semibold text-ponpon-navy/70 shadow-sm outline-none transition placeholder:text-ponpon-navy/50 focus:border-ponpon-coral focus:text-ponpon-navy focus:ring-2 focus:ring-ponpon-coral/20 ${isPasswordFieldError ? 'border-ponpon-coral ring-2 ring-ponpon-coral/30' : ''}`}
                  />
                  {error && !isEmailFieldError ? (
                    <p className="mt-1 text-xs font-medium text-ponpon-coral/60">{error}</p>
                  ) : null}
                </div>

                {isSignup && hasVerificationEmailSent ? (
                  <div className="text-center text-xs text-ponpon-navy/60">
                    Verification email sent. Didn't receive it?{' '}
                    <button
                      type="button"
                      disabled={isResendLoading}
                      onClick={handleResendVerification}
                      className="font-semibold text-ponpon-coral underline-offset-2 hover:underline disabled:opacity-60"
                    >
                      {isResendLoading ? 'Resending…' : 'Resend'}
                    </button>
                  </div>
                ) : null}

                {!isSignup && hasPasswordResetEmailSent ? (
                  <div className="text-center text-xs text-ponpon-navy/60">
                    Password reset email sent. Didn't receive it?{' '}
                    <button
                      type="button"
                      disabled={isResetResendLoading}
                      onClick={handleResendPasswordReset}
                      className="font-semibold text-ponpon-coral underline-offset-2 hover:underline disabled:opacity-60"
                    >
                      {isResetResendLoading ? 'Resending…' : 'Resend'}
                    </button>
                  </div>
                ) : null}

                <div className="mt-auto flex flex-col gap-3">
                  {isSignup ? (
                    <p className="text-center text-xs text-ponpon-navy/60">
                      By clicking Agree & Join, you agree to our{' '}
                      <Link
                        to="/terms"
                        onClick={() => {
                          try { setLoginPromptOpen?.(false); } catch (_) {}
                        }}
                        className="font-semibold text-ponpon-coral underline-offset-2 hover:underline"
                      >
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="/privacy"
                        onClick={() => {
                          try { setLoginPromptOpen?.(false); } catch (_) {}
                        }}
                        className="font-semibold text-ponpon-coral underline-offset-2 hover:underline"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  ) : null}

                  {!isSignup ? (
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={handlePasswordResetRequest}
                        className="text-xs font-semibold text-ponpon-coral underline-offset-2 transition hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                  ) : null}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex w-full items-center justify-center rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isLoading
                      ? isSignup
                        ? 'Creating account...'
                        : 'Signing in...'
                      : isSignup
                      ? 'Agree & Join'
                      : 'Sign in with email'}
                  </button>

                  {/* CHANGED: Google button per spec: h=40px, icon=20px, L/R=12px, gap=10px */}
                  <button
                    type="button"
                    disabled={isGoogleLoading}
                    onClick={handleGoogleSignIn}
                    className="flex h-10 w-full items-center justify-center gap-[10px] rounded-full bg-[#F2F2F2] px-3 text-sm leading-5 font-medium text-[#1F1F1F] disabled:cursor-not-allowed disabled:opacity-70"
                    aria-label="Sign in with Google"
                  >
                    <img src="/assets/googleicon.svg" alt="Google" className="h-10 w-10" aria-hidden="true" />
                    <span style={{ fontFamily: 'Roboto, ui-sans-serif, system-ui, -apple-system, Segoe UI, Helvetica, Arial, Noto Sans, "Apple Color Emoji", "Segoe UI Emoji"' }}>
                      {isGoogleLoading ? 'Connecting…' : 'Continue with Google'}
                    </span>
                  </button>

                  <p className="text-center text-xs text-ponpon-navy/60">
                    {isSignup ? 'Already have an account?' : "Don't have an account yet?"}{' '}
                    <button
                      type="button"
                      onClick={toggleMode}
                      className="font-semibold text-ponpon-coral underline-offset-2 hover:underline"
                    >
                      {isSignup ? 'Sign in' : 'Create one'}
                    </button>
                  </p>
                </div>
              </form>
            </div>
          </div>
          {hideExplore ? null : (
          <div className="flex w-full justify-center lg:col-span-2">
            <button
              type="button"
              onClick={skipLogin}
              className={`${isSignup ? '' : 'pointer-events-none invisible'} text-sm font-semibold text-ponpon-navy/70 underline-offset-2 transition hover:text-ponpon-coral hover:underline`}
            >
              Explore first
            </button>
          </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default UserLogin;
